import { Box, Stack, useTheme } from "@mui/material";
import { AttachmentDragAndDrop, AttachmentTile, Drawer } from "components";
import { uploadTicketAttachments } from "controller";
import { useSetAtom } from "jotai";
import React, { useCallback, useEffect, useState } from "react";
import { AttachmentFileData } from "shared/types";
import { v4 as uuidv4 } from "uuid";
import { AttachmentData } from "./attachmentType";
export const AttachmentDrawer = ({
  open,
  onClose,
  onSubmit,
  attachmentData,
  serviceType,
  attachmentType = "general",
}: {
  open: boolean;
  onClose: () => void;
  onSubmit?: (data: Array<AttachmentData>) => void;
  attachmentData?: Array<any>;
  serviceType: "ticket" | "project" | "task";
  attachmentType: "general" | "notes";
}) => {
  const theme = useTheme();
  const [fileData, setFileData] = useState<Array<AttachmentData>>([]);

  const [completedFileCount, setCompletedFileCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const removeImageFromList = (itemIndex: number) => {
    setFileData((prev) => prev.filter((item, index) => itemIndex != index));
    setCompletedFileCount((prev) => prev - 1);
  };

  useEffect(() => {
    if (completedFileCount === fileData.length) {
      setIsUploading(false);
    }
  }, [completedFileCount]);

  useEffect(() => {
    if (attachmentData) {
      const uploadedFileLength = attachmentData.filter(
        (item: any) => item.status === "Uploaded",
      ).length;
      setCompletedFileCount(uploadedFileLength);
      setFileData(attachmentData);
    }
  }, [attachmentData]);

  const onDrop = useCallback((acceptedFiles: any) => {
    const data: Array<AttachmentFileData> = acceptedFiles?.map((file: any) => {
      const key = uuidv4();
      return {
        key: key,
        name: file.name,
        size: file.size,
        type: file.type,
        status: "Uploading",
        progress: 0,
        file,
      };
    });
    setFileData((prev) => {
      return [...prev, ...data];
    });
    setIsUploading(true);
    data.map((file) => {
      uploadImage({ file: file.file, key: file.key });
    });
  }, []);

  const uploadImage = ({ file, key }: { file: any; key: string }) => {
    const formData = new FormData();
    formData.append("files", file);

    uploadTicketAttachments({
      formData,
      serviceType,
      attachmentType,
    }).subscribe({
      next: ({ response, status, percentage, controller }) => {
        if (status === "COMPLETE") {
          if (response?.success && response?.statusCode === 200) {
            setCompletedFileCount((prev) => prev + 1);
            setFileData((prev) => {
              const updatedData = [...prev];
              const index = updatedData.findIndex((file) => file.key === key);

              if (index !== -1) {
                updatedData[index] = {
                  ...updatedData[index],
                  status: "Uploaded",
                  progress: 100,
                  responseData: response.data[0],
                };
                return updatedData;
              }
              return updatedData;
            });
          } else {
            setCompletedFileCount((prev) => prev + 1);
            setFileData((prev) => {
              const updatedData = [...prev];
              const index = updatedData.findIndex((file) => file.key === key);

              if (index !== -1) {
                updatedData[index] = {
                  ...updatedData[index],
                  status: "Error",
                  progress: 0,
                };
                return updatedData;
              }
              return updatedData;
            });
          }
        } else {
          setFileData((prev) => {
            const updatedData = [...prev];
            const index = updatedData.findIndex((file) => file.key === key);
            if (index !== -1) {
              updatedData[index] = {
                ...updatedData[index],
                status: "Uploading",
                progress: percentage,
              };
              return updatedData;
            }
            return updatedData;
          });
        }
      },
    });
  };

  const handlePrimaryButtonClick = () => {
    onSubmit?.(fileData);
    // addAttachment(fileData);
    onClose();
  };
  return (
    <Drawer
      open={open}
      title="Attachment"
      onClose={onClose}
      primaryButtonText="Save"
      secondaryButtonText="Cancel"
      onSecondaryButtonClick={onClose}
      onPrimaryButtonClick={handlePrimaryButtonClick}
      isPrimaryButtonDisabled={
        completedFileCount !== fileData.length && isUploading
      }
      isSecondaryButtonDisabled={
        completedFileCount !== fileData.length && isUploading
      }
    >
      <Box px="32px" sx={{ width: "680px", pt: "24px" }}>
        <AttachmentDragAndDrop onDrop={onDrop} />
        <Box minHeight={"200px"} mt="16px">
          <Stack>
            {fileData.map((file, index) => (
              <AttachmentTile
                key={file.key}
                file={file}
                percentage={file.progress}
                status={file.status}
                onRemove={() => removeImageFromList(index)}
              />
            ))}
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
};
